import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, Sparkles, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const avatarStyles = [
  { id: "professional", name: "Professional", description: "Business portrait style" },
  { id: "artistic", name: "Artistic", description: "Creative and expressive" },
  { id: "cartoon", name: "Cartoon", description: "Fun and playful" },
  { id: "futuristic", name: "Futuristic", description: "Sci-fi cyberpunk" },
  { id: "fantasy", name: "Fantasy", description: "Magical and mystical" },
  { id: "minimalist", name: "Minimalist", description: "Clean and modern" },
];

const DashboardAvatarGenerator = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string>("professional");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAvatar, setGeneratedAvatar] = useState<string | null>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result as string);
      setGeneratedAvatar(null);
    };
    reader.readAsDataURL(file);
  };

  const generateAvatar = async () => {
    if (!uploadedImage) {
      toast.error("Please upload an image first");
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-avatar", {
        body: {
          imageUrl: uploadedImage,
          style: selectedStyle,
        },
      });

      if (error) {
        if (error.message.includes("429")) {
          toast.error("Rate limit exceeded. Please try again later.");
        } else if (error.message.includes("402")) {
          toast.error("Payment required. Please add credits to your workspace.");
        } else {
          throw error;
        }
        return;
      }

      if (data?.imageData) {
        setGeneratedAvatar(data.imageData);
        toast.success("Avatar generated successfully!");
      } else {
        throw new Error("No image data received");
      }
    } catch (error) {
      console.error("Error generating avatar:", error);
      toast.error("Failed to generate avatar. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadAvatar = () => {
    if (!generatedAvatar) return;

    const link = document.createElement("a");
    link.href = generatedAvatar;
    link.download = `avatar-${selectedStyle}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Avatar downloaded!");
  };

  const saveAsProfilePicture = async () => {
    if (!generatedAvatar) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Convert base64 to blob
      const response = await fetch(generatedAvatar);
      const blob = await response.blob();
      const fileName = `avatar-${user.id}-${Date.now()}.png`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, blob, {
          contentType: "image/png",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      toast.success("Profile picture updated!");
      setTimeout(() => navigate("/dashboard/profile"), 1000);
    } catch (error) {
      console.error("Error saving avatar:", error);
      toast.error("Failed to save avatar as profile picture");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-4xl font-bold">AI Avatar Generator</h1>
            <p className="text-muted-foreground mt-2">
              Create stunning AI-generated avatars from your photos
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl">
          {/* Upload & Style Selection */}
          <Card>
            <CardHeader>
              <CardTitle>1. Upload Your Photo</CardTitle>
              <CardDescription>Choose a clear photo of yourself</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full h-32"
                >
                  {uploadedImage ? (
                    <img
                      src={uploadedImage}
                      alt="Uploaded"
                      className="max-h-28 max-w-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8" />
                      <span>Click to upload image</span>
                    </div>
                  )}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-3">2. Select Style</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {avatarStyles.map((style) => (
                      <Button
                        key={style.id}
                        variant={selectedStyle === style.id ? "default" : "outline"}
                        onClick={() => setSelectedStyle(style.id)}
                        className="h-auto py-4 flex flex-col items-start"
                      >
                        <span className="font-semibold">{style.name}</span>
                        <span className="text-xs opacity-70">{style.description}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={generateAvatar}
                  disabled={!uploadedImage || isGenerating}
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Avatar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview & Actions */}
          <Card>
            <CardHeader>
              <CardTitle>3. Your AI Avatar</CardTitle>
              <CardDescription>
                {generatedAvatar ? "Your avatar is ready!" : "Your avatar will appear here"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aspect-square bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                {generatedAvatar ? (
                  <img
                    src={generatedAvatar}
                    alt="Generated Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Upload a photo and select a style to generate</p>
                  </div>
                )}
              </div>

              {generatedAvatar && (
                <div className="space-y-2">
                  <Button onClick={downloadAvatar} variant="outline" className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Download Avatar
                  </Button>
                  <Button onClick={saveAsProfilePicture} className="w-full">
                    Save as Profile Picture
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardAvatarGenerator;
